import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['build-a-shared-plan-with-your-partner'];

export default function SharedPlanWithPartnerPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['planning-for-kids-college-and-family-life-events', 'from-sign-up-to-a-real-fire-plan']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'A household plan becomes more useful when it is visible to both partners instead of living inside one person’s notes and assumptions.',
          'Per Diem treats the household as a shared planning unit rather than as a solo-user dashboard with extra context bolted on later.',
          'Collaboration matters because financial decisions often fail from misalignment before they fail from math.',
        ]}
      />

      <ArticleSection title="Many household plans are shared in theory and private in practice">
        <ArticleParagraph>
          Plenty of couples would say they make money decisions together. Fewer would say they have
          a truly shared system for reviewing the same assumptions, numbers, and tradeoffs in one
          place.
        </ArticleParagraph>
        <ArticleParagraph>
          What usually happens instead is that one person becomes the default operator. They manage
          the spreadsheet, the app stack, or the family financial memory, and the other person only
          sees a simplified summary from time to time.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.addPartner}
        alt="Per Diem add partner screen"
        caption="A shared plan starts by making the household itself explicit instead of assuming one person will always be the system."
      />

      <ArticleSection title="Why shared visibility changes the quality of decisions">
        <ArticleBullets
          items={[
            'Both partners can react to the same numbers instead of different mental models.',
            'Tradeoffs become easier to discuss when the assumptions are visible.',
            'The plan becomes more resilient because one person is not carrying the full context alone.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="Household planning should look like household planning">
        <ArticleParagraph>
          A FIRE plan is not just an individual optimization problem when the life being planned is
          shared. It has to account for joint goals, different risk tolerances, career decisions,
          and the family choices both people are making together.
        </ArticleParagraph>
        <ArticleParagraph>
          That is why Per Diem treats partner context as part of the product surface and not just a
          technical permission setting.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileHome}
        alt="Per Diem mobile home screen"
        caption="Shared planning is most useful when both partners can interpret the same high-level signals and discuss what they mean."
        variant="phone"
      />

      <ArticleSection title="The best household systems reduce hidden assumptions">
        <ArticleParagraph>
          Couples do not usually get in trouble because they cannot add. They get in trouble because
          their assumptions are invisible until a bigger decision exposes the mismatch. A shared plan
          helps surface those assumptions earlier and with less friction.
        </ArticleParagraph>
        <ArticleParagraph>
          That is the real value of household collaboration in Per Diem. It is less about account
          access than about shared financial clarity.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
